import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../lib/api';
import { formatRelativeTime, truncateText } from '../lib/utils';
import { ArrowRight } from 'lucide-react';
import { HomeHeader } from '../components/home/HomeHeader';

const STATS = [
  { key: 'ideas_pending', label: 'Pending ideas', emoji: '💡', to: '/ideas', testId: 'stat-pending-ideas', tone: 'bg-[#EBF3FA]' },
  { key: 'ideas_greenlit', label: 'Greenlit', emoji: '🟢', to: '/ideas', testId: 'stat-greenlit', tone: 'bg-[#ECFDF5]' },
  { key: 'ideas_in_production', label: 'In production', emoji: '🎬', to: '/production-packs', testId: 'stat-in-production', tone: 'bg-[#FFF7ED]' },
  { key: 'pending_approvals', label: 'Pending approvals', emoji: '⏳', to: '/production-packs', testId: 'stat-pending-approvals', tone: 'bg-[#FEF3C7]' },
];

const QUICK = [
  ['🛍️', 'Listings', '/etsy'], ['💡', 'Ideas', '/ideas'], ['🎬', 'Production', '/production-packs'],
  ['📅', 'Calendar', '/calendar'], ['📱', 'Story Sets', '/story-sets'], ['🎯', 'Ad Creatives', '/ad-creatives'],
];

const Panel = ({ title, link, linkLabel, children, testId }) => (
  <div className="wf-card p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-jakarta text-lg font-bold text-[#2D2623]">{title}</h2>
      <Link to={link} className="text-xs font-semibold text-[#C85A32] hover:underline inline-flex items-center gap-1">
        {linkLabel} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
    {children}
  </div>
);

const Empty = ({ emoji, text }) => (
  <div className="rounded-2xl border-2 border-dashed border-[#E8E1D5] py-8 text-center text-sm text-[#9A8E85]">
    <div className="text-3xl mb-1" aria-hidden>{emoji}</div>{text}
  </div>
);

const Dashboard = ({ embedded }) => {
  const { user, activeBrand } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeBrand) return;
    dashboardAPI.getStats(activeBrand)
      .then((r) => setStats(r.data))
      .catch((e) => console.error('Failed to fetch dashboard stats:', e))
      .finally(() => setLoading(false));
  }, [activeBrand]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="dashboard-loading">
        <div className="h-40 rounded-3xl bg-[#F0EAE1]" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-3xl bg-[#F0EAE1]" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {!embedded && (
        <HomeHeader emoji="✨" name={user?.name} subtitle="Your creative command center for turning ideas into stunning jewelry content 💎">
          <div className="flex flex-wrap gap-2 mt-5">
            <Link to="/ideas" className="rounded-xl bg-[#2D2623] text-white px-4 py-2.5 text-xs font-bold hover:bg-black transition-colors" data-testid="add-idea-btn">💡 New idea</Link>
            <Link to="/etsy" className="rounded-xl bg-white border border-[#E8E1D5] text-[#2D2623] px-4 py-2.5 text-xs font-bold hover:bg-[#F7F3EC] transition-colors" data-testid="open-listings-btn">🛍️ Open listings</Link>
          </div>
        </HomeHeader>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((s, i) => (
          <Link key={s.key} to={s.to} className={`wf-card wf-card-hover wf-rise p-5 ${s.tone}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={s.testId}>
            <div className="text-2xl" aria-hidden>{s.emoji}</div>
            <div className="text-3xl font-extrabold text-[#2D2623] mt-2">{stats?.[s.key] || 0}</div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#9A8E85]">{s.label}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="quick-links">
        {QUICK.map(([e, l, to]) => (
          <Link key={to} to={to} className="wf-card wf-card-hover p-4 text-center" data-testid={`quick-link-${l.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-2xl" aria-hidden>{e}</div>
            <div className="text-xs font-bold text-[#2D2623] mt-1">{l}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5">
          <Panel title="🌟 Recent inspo" link="/ideas" linkLabel="View all" testId="recent-inspo-section">
            {stats?.recent_inspo?.length ? (
              <div className="space-y-2">
                {stats.recent_inspo.map((inspo) => (
                  <div key={inspo.id} className="rounded-2xl bg-[#FDFBF7] border border-[#F0EAE1] p-3">
                    <p className="text-sm font-semibold text-[#2D2623]">{truncateText(inspo.notes, 60)}</p>
                    <p className="text-[11px] text-[#9A8E85] mt-0.5">{inspo.category} · {formatRelativeTime(inspo.created_at)}</p>
                  </div>
                ))}
              </div>
            ) : <Empty emoji="🌱" text="No inspo yet" />}
          </Panel>
        </div>
        <div className="lg:col-span-7">
          <Panel title="📅 Upcoming schedule" link="/calendar" linkLabel="View calendar" testId="upcoming-schedule-section">
            {stats?.upcoming_assets?.length ? (
              <div className="space-y-2">
                {stats.upcoming_assets.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-2xl bg-[#FDFBF7] border border-[#F0EAE1] p-3">
                    <span className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-lg" aria-hidden>📣</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2D2623]">{a.platform}</p>
                      <p className="text-[11px] text-[#9A8E85]">{a.category}</p>
                    </div>
                    <span className="text-xs font-bold text-[#2D2623]">{a.posting_date}</span>
                  </div>
                ))}
              </div>
            ) : <Empty emoji="🗓️" text="No scheduled content. Greenlight ideas to schedule them" />}
          </Panel>
        </div>
      </div>

      <div className="wf-card p-6">
        <h2 className="font-jakarta text-lg font-bold text-[#2D2623] mb-4">🎞️ Content pipeline</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-2xl bg-[#EEF2FF] p-4"><p className="text-2xl font-extrabold text-[#2D2623]">{stats?.ideas_scheduled || 0}</p><p className="text-[11px] uppercase tracking-wider font-bold text-[#9A8E85]">🗓️ Scheduled</p></div>
          <div className="rounded-2xl bg-[#ECFDF5] p-4"><p className="text-2xl font-extrabold text-[#2D2623]">{stats?.ideas_posted || 0}</p><p className="text-[11px] uppercase tracking-wider font-bold text-[#9A8E85]">🚀 Posted</p></div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
